import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { ROUTES } from '@/config/routes'
import { Spinner } from '@/components/ui/Spinner'
import type { UserRole } from '@/types'

interface RequireRoleProps {
  roles: UserRole[]
  children: React.ReactNode
}

/**
 * Route guard that renders children only if the current user's role is
 * in the `roles` allow-list. Redirects to `/login` when unauthenticated
 * and to `/unauthorized` when the role is insufficient.
 */
export function RequireRole({ roles, children }: RequireRoleProps) {
  const { user, loading } = useAuth()
  const location = useLocation()

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Spinner size="lg" />
      </div>
    )
  }

  if (!user) {
    const returnUrl = location.pathname + location.search
    return <Navigate to={`${ROUTES.AUTH.LOGIN}?returnUrl=${encodeURIComponent(returnUrl)}`} replace />
  }

  if (user.role === null || !roles.includes(user.role)) {
    return <Navigate to={ROUTES.UNAUTHORIZED} replace />
  }

  return <>{children}</>
}
